/**
 * Infinite scroll for collection product grids.
 *
 * Uses Shopify's Section Rendering API (the same `?section_id=` endpoint
 * `facets.js` already relies on) to fetch subsequent pages and append the
 * product cards Dawn/Shopify already generated. Because `<infinite-scroll-sentinel>`
 * is a custom element, it re-initializes automatically whenever facets.js
 * replaces #ProductGridContainer (filter/sort change), which naturally resets
 * infinite scroll state for the new result set without any extra wiring.
 */
class InfiniteScrollSentinel extends HTMLElement {
  connectedCallback() {
    this.loading = false;
    this.done = false;
    this.nextUrl = this.dataset.nextUrl || '';
    this.sectionId = this.dataset.sectionId || '';

    this.grid = this.closest('#ProductGridContainer')?.querySelector('#product-grid');
    this.paginationWrapper = this.parentElement?.querySelector('.pagination-wrapper');
    this.loadingEl = this.querySelector('.infinite-scroll-status__loading');
    this.endEl = this.querySelector('.infinite-scroll-status__end');

    if (!this.nextUrl || !this.sectionId || !this.grid) {
      this.done = true;
      return;
    }

    // Native pagination stays in the DOM (no-JS fallback / SEO / accessibility),
    // it's only visually hidden once infinite scroll has taken over successfully.
    if (this.paginationWrapper) this.paginationWrapper.classList.add('infinite-scroll-hide-pagination');

    this.observer = new IntersectionObserver(this.onIntersect.bind(this), {
      // Start fetching before the sentinel is actually on screen so the next
      // page is ready before the user reaches the bottom.
      rootMargin: '0px 0px 600px 0px',
      threshold: 0,
    });
    this.observer.observe(this);
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
  }

  onIntersect(entries) {
    const isVisible = entries.some((entry) => entry.isIntersecting);
    if (!isVisible || this.loading || this.done) return;
    this.loadNextPage();
  }

  loadNextPage() {
    this.loading = true;
    if (this.loadingEl) this.loadingEl.classList.remove('hidden');

    const separator = this.nextUrl.includes('?') ? '&' : '?';
    const url = `${this.nextUrl}${separator}section_id=${this.sectionId}`;

    fetch(url)
      .then((response) => {
        if (!response.ok) throw new Error(`Infinite scroll request failed with status ${response.status}`);
        return response.text();
      })
      .then((html) => this.appendProducts(html))
      .catch((error) => {
        console.warn('[infinite-scroll]', error);
        this.handleError();
      })
      .finally(() => {
        this.loading = false;
        if (this.loadingEl) this.loadingEl.classList.add('hidden');

        // The IntersectionObserver only fires on enter/exit transitions. On a
        // fast scroll or a tall viewport the sentinel can still be visible
        // after a page is appended, so force a re-check to keep loading.
        if (!this.done && this.observer) {
          this.observer.unobserve(this);
          this.observer.observe(this);
        }
      });
  }

  appendProducts(html) {
    const parsedDocument = new DOMParser().parseFromString(html, 'text/html');
    const newGrid = parsedDocument.querySelector('#product-grid');
    const newSentinel = parsedDocument.querySelector('infinite-scroll-sentinel');

    if (!newGrid) {
      this.handleError();
      return;
    }

    // Dawn renders the first couple of cards on any given page without
    // loading="lazy" (an "above the fold" heuristic meant for a freshly
    // loaded page). Here the cards are appended far below the fold, so force
    // lazy loading before they enter the live DOM. Only the src/loading
    // attribute is touched; srcset/sizes are left exactly as Dawn rendered them.
    newGrid.querySelectorAll('img').forEach((img) => {
      img.setAttribute('loading', 'lazy');
    });

    const fragment = document.createDocumentFragment();
    Array.from(newGrid.children).forEach((item) => fragment.appendChild(item));
    this.grid.appendChild(fragment);

    this.nextUrl = newSentinel ? newSentinel.dataset.nextUrl || '' : '';

    if (!this.nextUrl) this.finish();
  }

  finish() {
    this.done = true;
    if (this.observer) this.observer.disconnect();
    if (this.endEl) this.endEl.classList.remove('hidden');
  }

  handleError() {
    this.done = true;
    if (this.observer) this.observer.disconnect();
    // Fall back to normal, JS-free pagination navigation.
    if (this.paginationWrapper) this.paginationWrapper.classList.remove('infinite-scroll-hide-pagination');
  }
}

if (!customElements.get('infinite-scroll-sentinel')) {
  customElements.define('infinite-scroll-sentinel', InfiniteScrollSentinel);
}

/**
 * Small "scroll to top" button. Shown once the visitor has scrolled past a
 * threshold, hidden again near the top of the page.
 *
 * Also keeps clear of the Shopify Inbox chat launcher, which shares the
 * same bottom-right corner. Shopify Inbox renders as a <shopify-chat>
 * custom element with an open shadow root; its visible launcher lives
 * inside a wrapper Shopify positions with an inline `bottom` offset that
 * varies by browser (observed to differ between desktop, mobile Safari,
 * and in-app browsers such as Instagram's). Rather than hardcoding a
 * guessed offset, the launcher's actual rendered position is measured at
 * runtime and this button is placed just above it.
 */
function findShopifyChatLauncher() {
  const host = document.querySelector('shopify-chat');
  if (!host || !host.shadowRoot) return null;

  const candidates = host.shadowRoot.querySelectorAll('*');
  for (const candidate of candidates) {
    const style = getComputedStyle(candidate);
    if ((style.position === 'fixed' || style.position === 'absolute') && style.bottom !== 'auto') {
      return candidate;
    }
  }
  return null;
}

class BackToTopButton extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('button');
    if (!this.button) return;

    this.threshold = 500;
    this.visible = false;
    this.ticking = false;

    this.onScroll = this.onScroll.bind(this);
    this.onClick = this.onClick.bind(this);
    this.updateChatAvoidance = this.updateChatAvoidance.bind(this);

    this.button.addEventListener('click', this.onClick);
    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.updateChatAvoidance);
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', this.updateChatAvoidance);
    }
    this.onScroll();
    this.watchForChatLauncher();
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.updateChatAvoidance);
    if (window.visualViewport) {
      window.visualViewport.removeEventListener('resize', this.updateChatAvoidance);
    }
    if (this.chatBodyObserver) this.chatBodyObserver.disconnect();
  }

  watchForChatLauncher() {
    const existing = findShopifyChatLauncher();
    if (existing) {
      this.chatLauncher = existing;
      this.updateChatAvoidance();
      return;
    }

    this.chatBodyObserver = new MutationObserver(() => {
      const launcher = findShopifyChatLauncher();
      if (launcher) {
        this.chatLauncher = launcher;
        this.chatBodyObserver.disconnect();
        this.updateChatAvoidance();
      }
    });
    this.chatBodyObserver.observe(document.body, { childList: true, subtree: true });
  }

  updateChatAvoidance() {
    if (!this.chatLauncher || !this.chatLauncher.isConnected) {
      this.chatLauncher = findShopifyChatLauncher();
    }

    if (!this.chatLauncher) {
      this.button.style.removeProperty('bottom');
      return;
    }

    const gap = 16;
    const launcherRect = this.chatLauncher.getBoundingClientRect();
    const offset = window.innerHeight - launcherRect.top + gap;
    this.button.style.setProperty('bottom', offset + 'px');
  }

  onScroll() {
    if (this.ticking) return;
    this.ticking = true;
    window.requestAnimationFrame(() => {
      const shouldShow = window.scrollY > this.threshold;
      if (shouldShow !== this.visible) {
        this.visible = shouldShow;
        this.button.hidden = !shouldShow;
      }
      this.ticking = false;
    });
  }

  onClick() {
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({
      top: 0,
      behavior: reduceMotion ? 'auto' : 'smooth',
    });
  }
}

if (!customElements.get('back-to-top-button')) {
  customElements.define('back-to-top-button', BackToTopButton);
}
